import React from "react";

/* Plain-English legal drafts tailored to the WebJesters client portal.
   NOT legal advice — have Arizona counsel review before publishing. */

const H = ({ children }) => <h2 className="text-lg font-semibold text-zinc-100 pt-2">{children}</h2>;
const P = ({ children }) => <p>{children}</p>;
const UL = ({ items }) => (
    <ul className="list-disc pl-5 space-y-1.5 marker:text-zinc-600">
        {items.map((t, i) => (
            <li key={i}>{t}</li>
        ))}
    </ul>
);

export function PrivacyPolicy({ info }) {
    return (
        <>
            <P>
                This Privacy Policy explains how {info.entity_name} ("WebJesters", "we", "us") collects, uses, shares, and
                protects information through the WebJesters client portal at portal.webjesters.com (the "Portal") and the
                lead-intake forms on websites we build and host for our clients.
            </P>
            <H>1. Who we are</H>
            <P>
                {info.entity_name} builds and manages websites for local service businesses. The Portal is where our clients
                manage their business information, photos, website change requests, and the leads their website generates.
            </P>
            <H>2. Information we collect</H>
            <UL
                items={[
                    "Account data: name, business name, email address, password (stored only as a salted bcrypt hash), and login activity (timestamps, IP address, device/browser type) for security.",
                    "Business profile data you submit: hours, service areas, services, promotional copy, testimonials, social links, logo, and photos.",
                    "Lead data: name, phone, email, requested service, and message submitted by visitors through your website's contact form. You are the business owner of that information; we process it on your behalf.",
                    "Payment data: handled entirely by Stripe. We receive confirmation of purchase, the package purchased, and your email — never full card numbers.",
                    "Text-message preferences: your mobile number and your consent record (date, time, IP, and wording) if you opt in to SMS alerts.",
                    "Technical data: server logs, security audit events, and strictly-necessary authentication cookies. We do not use advertising or analytics tracking cookies in the Portal.",
                ]}
            />
            <H>3. How we use information</H>
            <UL
                items={[
                    "To provide, secure, and support the Portal and your website.",
                    "To deliver leads to you and, if you opt in, to send you text alerts about new leads.",
                    "To send transactional emails (welcome, password reset, account notices).",
                    "To detect, investigate, and prevent fraud, abuse, and security incidents.",
                    "To comply with legal obligations and enforce our Terms.",
                ]}
            />
            <P>We do not sell personal information and we do not share it with third parties for their own marketing.</P>
            <H>4. Service providers we share data with</H>
            <UL
                items={[
                    "Stripe, Inc. — payment processing.",
                    "Resend — transactional email delivery.",
                    "Twilio, Inc. — SMS delivery (only if you opt in).",
                    "Emergent / cloud infrastructure providers — application hosting, database, and encrypted object storage for photos and logos.",
                    "Cloudflare — network security and performance.",
                ]}
            />
            <P>Each provider is bound by contract to process data only on our instructions and to protect it appropriately.</P>
            <H>5. Text messages (TCPA)</H>
            <P>
                SMS alerts require a separately enabled paid add-on and are strictly opt-in. By enabling text alerts in the Portal you consent to receive automated
                new-lead and website build-status notifications at the mobile number you provide. Message frequency varies.
                Message and data rates may apply. Reply STOP at any time to cancel, or turn alerts off in Account &amp;
                privacy. Reply START to re-subscribe while your paid add-on is active. START does not purchase or activate an add-on. Consent is not a condition of purchase.
            </P>
            <H>6. Security</H>
            <P>
                We use HTTPS everywhere, HttpOnly/Secure session cookies, bcrypt password hashing, per-client data isolation,
                brute-force lockouts, rate limiting, administrative audit logging, and least-privilege access. No method of
                transmission or storage is 100% secure, but we work hard to protect your data and review our safeguards
                regularly.
            </P>
            <H>7. Data retention</H>
            <UL
                items={[
                    "Active accounts: retained for as long as your account is open.",
                    "Closed accounts: moved to a recoverable state for 30 days, then permanently deleted (including photos and leads) by an automated job.",
                    "Security and audit logs: retained for up to 24 months.",
                    "Payment records: retained as required by tax and accounting law.",
                ]}
            />
            <H>8. Your choices and rights</H>
            <P>
                You can view and update your business information at any time in the Portal, export a complete copy of your
                workspace data from Account &amp; privacy, turn SMS alerts on or off, and request account deletion by emailing{" "}
                {info.privacy_email}. We honor applicable state privacy rights (for example access, correction, deletion,
                and portability) without discrimination.
            </P>
            <H>9. Data breach notification</H>
            <P>
                If a security incident affects your personal information, we will notify you without unreasonable delay and
                within the timeframes required by Arizona law (A.R.S. § 18-552) and any other applicable law, and will
                notify regulators where required.
            </P>
            <H>10. Children</H>
            <P>The Portal is a business tool and is not directed to anyone under 18. We do not knowingly collect data from children.</P>
            <H>11. Changes</H>
            <P>
                We will post any changes here and update the effective date. Material changes will be announced in the Portal
                and may require re-acceptance.
            </P>
            <H>12. Contact</H>
            <P>
                {info.entity_name} · {info.mailing_address} · {info.privacy_email}
            </P>
        </>
    );
}

export function TermsOfService({ info }) {
    return (
        <>
            <P>
                These Terms of Service ("Terms") govern your access to and use of the WebJesters client portal and related
                services (the "Services") provided by {info.entity_name}. By signing in or using the Services you agree to
                these Terms on behalf of yourself and the business you represent.
            </P>
            <H>1. Accounts and access</H>
            <UL
                items={[
                    "Accounts are provisioned by WebJesters for paying clients. You are responsible for keeping your password confidential and for all activity under your account.",
                    "You must use a strong password and notify us immediately of any unauthorized access.",
                    "We may suspend access to protect the Services, other clients, or to comply with law.",
                ]}
            />
            <H>2. Your content</H>
            <P>
                You retain ownership of the business information, photos, logos, testimonials, and leads you provide. You
                grant WebJesters a non-exclusive license to host, display, and reproduce that content solely to build,
                operate, and improve your website and the Services. You represent that you have the rights and any required
                consents (including from the people in photos and testimonials) to share that content.
            </P>
            <H>3. Acceptable use</H>
            <UL
                items={[
                    "No unlawful, deceptive, infringing, or harmful content.",
                    "No attempts to access other clients' data, probe or circumvent security, or overload the Services.",
                    "No use of lead data in violation of the TCPA, CAN-SPAM, or other marketing laws — you are responsible for how you contact your leads.",
                ]}
            />
            <H>4. Website build and change requests</H>
            <P>
                Website deliverables, timelines, and fees are described in your order or proposal. Change requests submitted
                through the Portal are handled in the order received; we will tell you if a request falls outside your plan.
            </P>
            <H>5. Fees and payments</H>
            <P>
                Fees are charged through Stripe per your order. Deposits are non-refundable once build work has begun unless
                otherwise stated in writing. Late or failed payments may result in suspension of the Services.
            </P>
            <H>6. Text message alerts</H>
            <P>
                SMS alerts are an optional paid add-on and are not included with every Portal account. WebJesters must
                activate the add-on separately; recipient consent is still required. By opting in you agree to the SMS
                terms in our Privacy Policy. Reply STOP to stop messages; stopping messages does not by itself cancel
                your paid add-on agreement. Contact WebJesters separately to cancel the add-on.
            </P>
            <H>7. Confidentiality and data protection</H>
            <P>
                Each party will protect the other's confidential information. Our handling of personal data is described in
                the Privacy Policy and the Data Processing Addendum, which are part of these Terms.
            </P>
            <H>8. Disclaimers</H>
            <P>
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE". TO THE FULLEST EXTENT PERMITTED BY LAW, WEBJESTERS
                DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
                NON-INFRINGEMENT. WE DO NOT GUARANTEE ANY PARTICULAR VOLUME OF LEADS, SEARCH RANKING, OR BUSINESS RESULT.
            </P>
            <H>9. Limitation of liability</H>
            <P>
                TO THE FULLEST EXTENT PERMITTED BY LAW, WEBJESTERS WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL,
                CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR LOST PROFITS OR REVENUE. OUR TOTAL LIABILITY FOR ANY CLAIM IS LIMITED
                TO THE FEES YOU PAID US IN THE TWELVE (12) MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM.
            </P>
            <H>10. Indemnity</H>
            <P>
                You will defend and indemnify WebJesters against claims arising from your content, your use of lead data, or
                your breach of these Terms.
            </P>
            <H>11. Termination</H>
            <P>
                Either party may terminate on written notice. On termination your account enters a 30-day recoverable state
                during which you may export your data; after that it is permanently deleted.
            </P>
            <H>12. Governing law and venue</H>
            <P>
                These Terms are governed by the laws of the State of Arizona, without regard to conflict-of-laws rules. The
                parties consent to the exclusive jurisdiction of the state and federal courts located in {info.venue}. Either
                party may seek injunctive relief for misuse of confidential information or intellectual property.
            </P>
            <H>13. General</H>
            <P>
                These Terms, the Privacy Policy, the DPA, and your order form are the entire agreement. If any provision is
                unenforceable the rest remains in effect. We may update these Terms; continued use after notice is acceptance.
            </P>
            <H>14. Contact</H>
            <P>
                {info.entity_name} · {info.mailing_address} · {info.privacy_email}
            </P>
        </>
    );
}

export function DataProcessingAddendum({ info }) {
    return (
        <>
            <P>
                This Data Processing Addendum ("DPA") forms part of the Terms of Service between {info.entity_name}
                ("Processor" or "WebJesters") and the client business ("Controller" or "you"). It applies whenever WebJesters
                processes personal information of your customers and prospects (for example, leads submitted through your
                website) on your behalf.
            </P>
            <H>1. Roles</H>
            <P>
                You determine why and how your customers' personal information is collected through your website; WebJesters
                processes it only to deliver it to you through the Portal and to operate the Services. For your own account
                data, WebJesters acts as an independent business as described in the Privacy Policy.
            </P>
            <H>2. Processing instructions</H>
            <P>
                WebJesters will process personal information only on your documented instructions — which are the Terms,
                this DPA, and your use of Portal features — unless required by law, in which case we will notify you where
                permitted.
            </P>
            <H>3. Confidentiality</H>
            <P>WebJesters personnel with access to personal information are bound by confidentiality obligations and access is limited to what is necessary.</P>
            <H>4. Security measures</H>
            <UL
                items={[
                    "Encryption in transit (TLS 1.2+) for all Portal and API traffic.",
                    "Per-client logical isolation of all records; every query is scoped to the client workspace.",
                    "Hashed credentials, short-lived session tokens with revocation, brute-force lockout, and rate limiting.",
                    "Audit logging of privileged administrative actions (who accessed which client's data and when).",
                    "Automated retention enforcement: closed workspaces are permanently deleted 30 days after closure.",
                ]}
            />
            <H>5. Sub-processors</H>
            <P>
                You authorize the sub-processors listed in the Privacy Policy (payments, email, SMS, hosting/storage, network
                security). We will update that list before adding a new sub-processor and you may object on reasonable
                grounds.
            </P>
            <H>6. Assistance</H>
            <P>
                WebJesters will reasonably assist you in responding to requests from your customers to access, correct,
                delete, or export their information, using the export and deletion tools in the Portal where possible.
            </P>
            <H>7. Security incidents</H>
            <P>
                WebJesters will notify you without undue delay, and in any case within 72 hours, after confirming a breach
                of security affecting personal information processed on your behalf, and will provide the information
                reasonably needed for you to meet your own notification obligations (including under A.R.S. § 18-552).
            </P>
            <H>8. Deletion and return</H>
            <P>
                You may export your workspace data at any time. After termination, data is held for 30 days and then
                permanently deleted, except where retention is required by law.
            </P>
            <H>9. Contact</H>
            <P>
                {info.entity_name} · {info.mailing_address} · {info.privacy_email}
            </P>
        </>
    );
}
